<template>
  <UPageSection
    :dir="dir"
    :title="resolvedTitle"
    :description="resolvedSubtitle"
    :ui="{
      root: 'min-w-0 overflow-x-clip',
      container: 'w-full max-w-full min-w-0 py-10 sm:py-14',
      title: 'text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl text-center',
      description: 'mx-auto mt-4 max-w-3xl text-lg text-gray-500 dark:text-gray-400 text-center',
    }"
  >
    <div
      v-if="groups.length"
      class="grid gap-4 sm:grid-cols-2 xl:grid-cols-3"
    >
      <NuxtLink
        v-for="group in groups"
        :key="group.id"
        :to="`/services/${encodeURIComponent(group.slug)}`"
        class="group overflow-hidden rounded-2xl border border-default bg-default transition hover:border-primary/40 hover:shadow-lg"
        @click="navigatePreview($event, `/services/${encodeURIComponent(group.slug)}`)"
      >
        <div class="relative h-40 bg-elevated">
          <img
            v-if="group.coverUrl"
            :src="group.coverUrl"
            :alt="group.title"
            class="size-full object-cover transition duration-300 group-hover:scale-[1.02]"
          >
          <div
            v-else
            class="flex size-full items-center justify-center text-primary"
          >
            <UIcon
              :name="group.icon || 'i-lucide-layers'"
              class="size-10"
            />
          </div>
        </div>
        <div class="p-5">
          <h3 class="font-semibold text-highlighted">
            {{ group.title || (isFa ? 'گروه خدمت' : 'Service group') }}
          </h3>
          <p class="mt-2 line-clamp-3 min-h-18 text-sm leading-6 text-muted">
            {{ group.excerpt || (isFa ? 'برای مشاهدهٔ خدمات این گروه انتخاب کنید.' : 'Explore services in this group.') }}
          </p>
          <span class="mt-4 inline-flex items-center gap-2 text-sm font-medium text-primary">
            {{ isFa ? 'مشاهدهٔ خدمات گروه' : 'View group services' }}
            <UIcon
              :name="dir === 'rtl' ? 'i-lucide-arrow-left' : 'i-lucide-arrow-right'"
              class="size-4"
            />
          </span>
        </div>
      </NuxtLink>
    </div>
    <p
      v-else
      class="text-center text-sm text-muted"
    >
      {{ isFa ? 'گروه خدمت منتشرشده‌ای برای نمایش نیست.' : 'No published service groups to show.' }}
    </p>
  </UPageSection>
</template>

<script setup lang="ts">
type PublicServiceGroup = {
  id: string
  kind: string
  slug: string
  title: string
  excerpt: string
  coverUrl: string
  icon: string
}

type LocalizedText = string | Record<string, string>

const props = withDefaults(defineProps<{
  title?: LocalizedText
  subtitle?: LocalizedText
  builderPreview?: boolean
  onNavigate?: (to: string) => void
}>(), {
  title: undefined,
  subtitle: undefined,
})

const dir = useDir()
const locale = useState<string>('page-locale', () => 'fa')
const isFa = computed(() => locale.value === 'fa')
const requestFetch = useRequestFetch()
const { data, error } = await useAsyncData(
  'published-service-groups',
  () => requestFetch<{ items: PublicServiceGroup[] }>('/api/services'),
)

if (error.value) throw error.value

onMounted(() => refreshNuxtData('published-service-groups'))

const groups = computed(() =>
  (data.value?.items ?? []).filter(group => group.kind === 'group' && group.slug),
)

function localize(value?: LocalizedText): string {
  if (typeof value === 'string') return value
  if (!value) return ''
  return value[locale.value] ?? value.fa ?? value.en ?? ''
}

const resolvedTitle = computed(() => localize(props.title))
const resolvedSubtitle = computed(() => localize(props.subtitle))

function navigatePreview(event: MouseEvent, to: string) {
  if (!props.builderPreview) return
  event.preventDefault()
  props.onNavigate?.(to)
}
</script>
